import { useAuth } from "@/auth/hooks/useAuth";
import { useEffect, useState } from "react";

interface UseFetchDataReturn<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

const waitForToken = async (maxWaitMs = 500, intervalMs = 50) => {
  const endTime = Date.now() + maxWaitMs;
  let token =
    localStorage.getItem("jwtToken") || sessionStorage.getItem("jwtToken");
  while (!token && Date.now() < endTime) {
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
    token =
      localStorage.getItem("jwtToken") || sessionStorage.getItem("jwtToken");
  }
  return token;
};

export function useFetchData<T>(endpoint: string): UseFetchDataReturn<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const { user } = useAuth();
  if (user?.status === 2) {
    localStorage.removeItem("jwtToken");
    localStorage.removeItem("refreshToken");
    sessionStorage.removeItem("jwtToken");
    sessionStorage.removeItem("refreshToken");
    window.location.href = "/login";
  }
  const fetchWithToken = async (token: string) => {
    const response = await fetch(endpoint, {
      method: "GET",
      cache: "no-store",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    return response;
  };

  const refreshToken = async () => {
    const refreshToken =
      localStorage.getItem("refreshToken") ||
      sessionStorage.getItem("refreshToken");
    if (!refreshToken) throw new Error("No refresh token available");

    const response = await fetch(
      `${import.meta.env.VITE_API_ENDPOINT}/token/refresh`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${refreshToken}`,
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      }
    );

    if (!response.ok) throw new Error("Failed to refresh token");

    const data = await response.json();
    const newToken = data.token;
    const newRefreshToken = data.refresh_token;

    // Met à jour le token dans le localStorage
    localStorage.setItem("jwtToken", newToken);
    localStorage.setItem("refreshToken", newRefreshToken);

    return newToken;
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        let token =
          localStorage.getItem("jwtToken") ||
          sessionStorage.getItem("jwtToken");

        const pathname = window.location.pathname;
        const isPublicPath =
          pathname === "/login" ||
          pathname.startsWith("/authentification-cfp/") ||
          pathname.includes("submit/user-partner-subscription/") ||
          pathname.includes("submit/user-subscription/") ||
          pathname.includes("submit/cfp-subscription/");

        if (!token) {
          if (!isPublicPath) {
            token = await waitForToken();
            if (!token) {
              window.location.href = "/login";
              return;
            }
          } else {
            setLoading(false);
            return;
          }
        }

        let response = await fetchWithToken(token);

        if (response.status === 401) {
          // Le token est expiré, essayez de le renouveler
          token = await refreshToken();
          if (token) {
            response = await fetchWithToken(token);
          }
        }

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const result = await response.json();
        setData(result);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint]);

  return { data, loading, error };
}
